/**
 * Whether this tab can place a browser call right now.
 *
 * - `ready`: this tab holds the user's Twilio Device and can dial on it: registered, or registration
 *   timed out on a live token and the call is tried anyway (outbound calls do not need registration).
 * - `busy-in-another-tab`: another Welby tab of the same user is on a call, so the Device is not
 *   taken from it (doing so would drop that call).
 * - `unavailable`: no Device could be created, or its token has expired (network, token or Twilio problem).
 */
export type CallReadiness = 'ready' | 'busy-in-another-tab' | 'unavailable';

/** Shown to the care manager when a call cannot be placed. */
export const CALL_READINESS_MESSAGES: Record<Exclude<CallReadiness, 'ready'>, string> = {
  'busy-in-another-tab': 'You are on a call in another Welby tab. Finish that call first, or place this one from that tab.',
  unavailable: 'Calling is not ready yet. Check your connection and try again in a few seconds.',
};

/**
 * Messages tabs exchange on the BroadcastChannel before one takes over the Device from another.
 *
 * A `probe` asks the tab that holds the Device whether it is on a call; only that tab answers, with
 * `probe-reply`. `requestId` pairs a reply with its probe when several tabs probe at once. `idle` is
 * sent by the holder when its call ends, so a tab that was refused the Device can take it then.
 */
export type TwilioDeviceChannelMessage =
  | { type: 'probe'; requestId: string }
  | { type: 'probe-reply'; requestId: string; busy: boolean }
  | { type: 'idle' };

/** What the coordinator needs from the Twilio service. */
export interface TwilioDeviceCoordinatorHooks {
  /** This tab now holds the Device: create and register it. */
  onAcquired: () => void;
  /** Another tab took the Device: destroy this tab's one. */
  onLost: () => void;
  /** True while this tab's Device is on, ringing with, or about to place a call: it must not be taken. */
  isBusy: () => boolean;
  /** The holder's call ended; a tab that wanted the Device may take it now. */
  onHolderIdle: () => void;
}
